import { useState, useMemo, useEffect, useCallback } from "react";
import api from "../api/api";

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_PROVIDERS = [
  {
    id: 1, display_name: "Juan Pérez Plomería", legal_name: "Juan Carlos Pérez",
    dni: "28456789", phone: "+54 9 11 4523-7890", email: "juan@example.com",
    status: "active", rating_avg: 4.8, jobs_completed: 47,
    identity_verified: true, phone_verified: true,
    created_at: "2025-11-10",
    services: [
      { id: 1, label: "Plomería",  status: "approved",          validation_level: "B" },
      { id: 2, label: "Gasista",   status: "pending_documents", validation_level: "A" },
    ],
    documents: [
      { id: 1, type: "DNI",              status: "approved",      service: "Plomería",  uploaded_at: "2025-11-12" },
      { id: 2, type: "Matrícula",        status: "approved",      service: "Plomería",  uploaded_at: "2025-11-12" },
      { id: 3, type: "Cert. Gasista",    status: "pending",       service: "Gasista",   uploaded_at: "2025-12-01" },
    ],
  },
  {
    id: 2, display_name: "María Gómez Electricidad", legal_name: "María Laura Gómez",
    dni: "32145678", phone: "+54 9 351 6789-0123", email: "maria@example.com",
    status: "pending_review", rating_avg: null, jobs_completed: 0,
    identity_verified: false, phone_verified: true,
    created_at: "2026-01-15",
    services: [
      { id: 3, label: "Electricidad", status: "pending_review", validation_level: "C" },
    ],
    documents: [
      { id: 4, type: "DNI",           status: "pending",  service: "Electricidad", uploaded_at: "2026-01-16" },
      { id: 5, type: "Matrícula",     status: "pending",  service: "Electricidad", uploaded_at: "2026-01-16" },
    ],
  },
  {
    id: 3, display_name: "Carlos Ruiz Pintura", legal_name: "Carlos Alberto Ruiz",
    dni: "25789012", phone: "+54 9 11 2345-6789", email: "carlos@example.com",
    status: "draft", rating_avg: null, jobs_completed: 0,
    identity_verified: false, phone_verified: false,
    created_at: "2026-02-20",
    services: [
      { id: 4, label: "Pintura", status: "pending_documents", validation_level: "C" },
    ],
    documents: [],
  },
  {
    id: 4, display_name: "Ana Torres Limpieza", legal_name: "Ana Paula Torres",
    dni: "35678901", phone: "+54 9 11 8765-4321", email: "ana@example.com",
    status: "suspended", rating_avg: 3.2, jobs_completed: 12,
    identity_verified: true, phone_verified: true,
    created_at: "2025-09-05",
    services: [
      { id: 5, label: "Limpieza", status: "approved", validation_level: "C" },
    ],
    documents: [
      { id: 6, type: "DNI", status: "approved", service: "Limpieza", uploaded_at: "2025-09-06" },
    ],
  },
  {
    id: 5, display_name: "Roberto Silva Gasista", legal_name: "Roberto Alejandro Silva",
    dni: "29012345", phone: "+54 9 341 4567-8901", email: "roberto@example.com",
    status: "active", rating_avg: 4.5, jobs_completed: 89,
    identity_verified: true, phone_verified: true,
    created_at: "2025-07-18",
    services: [
      { id: 6, label: "Gasista",   status: "approved", validation_level: "A" },
      { id: 7, label: "Plomería",  status: "approved", validation_level: "B" },
    ],
    documents: [
      { id: 7, type: "DNI",           status: "approved", service: "Gasista",  uploaded_at: "2025-07-19" },
      { id: 8, type: "Habilitación",  status: "approved", service: "Gasista",  uploaded_at: "2025-07-19" },
      { id: 9, type: "Matrícula",     status: "approved", service: "Plomería", uploaded_at: "2025-07-20" },
    ],
  },
];

const STATUS_CONFIG = {
  active:            { label: "Activo",           color: "#1b7f4e", bg: "#e8f7ef" },
  pending_review:    { label: "En revisión",       color: "#b06a00", bg: "#fff3e0" },
  pending_documents: { label: "Docs pendientes",   color: "#b06a00", bg: "#fff3e0" },
  draft:             { label: "Borrador",          color: "#5c7a8a", bg: "#f0f5f8" },
  suspended:         { label: "Suspendido",        color: "#c0392b", bg: "#fdecea" },
  rejected:          { label: "Rechazado",         color: "#c0392b", bg: "#fdecea" },
  approved:          { label: "Aprobado",          color: "#1b7f4e", bg: "#e8f7ef" },
  pending:           { label: "Pendiente",         color: "#b06a00", bg: "#fff3e0" },
  expired:           { label: "Vencido",           color: "#7b5ea7", bg: "#f3eeff" },
};

const PROFILE_STATUS_ACTIONS = {
  active:         [{ action: "suspended", label: "Suspender",  color: "#c0392b" }, { action: "rejected", label: "Rechazar", color: "#7b5ea7" }],
  pending_review: [{ action: "active",    label: "Aprobar",    color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  draft:          [{ action: "active",    label: "Activar",    color: "#1b7f4e" }],
  suspended:      [{ action: "active",    label: "Reactivar",  color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  rejected:       [{ action: "active",    label: "Reactivar",  color: "#1b7f4e" }],
};

const DOC_STATUS_ACTIONS = {
  null:     [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  pending:  [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  approved: [{ action: "rejected", label: "Rechazar", color: "#c0392b" }],
  rejected: [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }],
};

// ─── Shared ───────────────────────────────────────────────────────────────────
function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#5c7a8a", bg: "#f0f5f8" };
  return (
    <span style={{
      padding: "3px 9px", borderRadius: 20, fontSize: 10,
      fontWeight: 700, letterSpacing: "0.05em",
      color: cfg.color, background: cfg.bg,
      display: "inline-block", whiteSpace: "nowrap",
    }}>
      {cfg.label.toUpperCase()}
    </span>
  );
}

function ActionBtn({ label, color, onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        padding: "5px 12px", borderRadius: 6, border: `1.5px solid ${color}`,
        background: "transparent", color, cursor: loading ? "not-allowed" : "pointer",
        fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 11,
        transition: "all 0.15s", opacity: loading ? 0.6 : 1,
      }}
      onMouseEnter={e => { if (!loading) { e.target.style.background = color; e.target.style.color = "#fff"; }}}
      onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = color; }}
    >
      {loading ? "..." : label}
    </button>
  );
}

const inputStyle = {
  padding: "9px 12px", borderRadius: 8, border: "1.5px solid #cddae4",
  fontSize: 13, fontFamily: "Montserrat, sans-serif", color: "#163244",
  background: "#fff", outline: "none", boxSizing: "border-box",
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function ProviderDrawer({ provider, onClose, onUpdate }) {
  const [loadingProfile, setLoadingProfile] = useState(null);
  const [loadingDoc, setLoadingDoc]         = useState(null);
  const [localProvider, setLocalProvider]   = useState({ documents: [], services: [], ...provider });
  const [rejectModal, setRejectModal]       = useState(null);
  const [rejectReason, setRejectReason]     = useState("");

  // Sincronizar cuando llegan datos frescos del backend (el padre hace GET y actualiza el prop)
  useEffect(() => {
    if (provider && (provider.services?.length > 0 || provider.documents?.length > 0)) {
      setLocalProvider({ documents: [], services: [], ...provider });
    }
  }, [provider]);

  const handleProfileStatus = async (newStatus) => {
    if (newStatus === "rejected") { setRejectModal({ type: "profile" }); return; }
    setLoadingProfile(newStatus);
    try {
      await api.patch(`/providers/admin-op/${localProvider.id}/profile-status`, { status: newStatus });
      const updated = { ...localProvider, status: newStatus };
      setLocalProvider(updated);
      onUpdate(updated);
    } catch (err) {
      console.error("Error al cambiar estado del perfil:", err);
    } finally {
      setLoadingProfile(null);
    }
  };

  const handleServiceStatus = async (serviceId, newStatus) => {
    try {
      await api.patch(`/providers/admin-op/${localProvider.id}/services/${serviceId}/status`, { status: newStatus });
      const updatedServices = localProvider.services.map(s =>
        s.id === serviceId ? { ...s, status: newStatus } : s
      );
      const updated = { ...localProvider, services: updatedServices };
      setLocalProvider(updated);
      onUpdate(updated);
    } catch (err) {
      console.error("Error al cambiar estado del servicio:", err);
    }
  };

  const handleDocStatus = async (docId, newStatus) => {
    if (newStatus === "rejected") { setRejectModal({ type: "doc", docId }); return; }
    setLoadingDoc(docId + newStatus);
    try {
      const res = await api.patch(`/providers/admin-op/${localProvider.id}/documents/${docId}/status`, { status: newStatus });
      const updatedDocs = localProvider.documents.map(d =>
        d.id === docId ? { ...d, verification_status: newStatus, status: newStatus } : d
      );
      // Si el backend devuelve el servicio actualizado automáticamente, actualizarlo también
      const updatedServices = res.data.updated_service
        ? localProvider.services.map(s => s.id === res.data.updated_service.id ? res.data.updated_service : s)
        : localProvider.services;
      const updated = { ...localProvider, documents: updatedDocs, services: updatedServices };
      setLocalProvider(updated);
      onUpdate(updated);
    } catch (err) {
      console.error("Error al cambiar estado del documento:", err);
    } finally {
      setLoadingDoc(null);
    }
  };

  const confirmReject = async () => {
    const { type, docId } = rejectModal;
    setRejectModal(null);

    if (type === "profile") {
      setLoadingProfile("rejected");
      try {
        await api.patch(`/providers/admin-op/${localProvider.id}/profile-status`, {
          status: "rejected",
          reason: rejectReason,
        });
        const updated = { ...localProvider, status: "rejected" };
        setLocalProvider(updated);
        onUpdate(updated);
      } catch (err) {
        console.error("Error al rechazar perfil:", err);
      } finally {
        setLoadingProfile(null);
      }
    } else {
      setLoadingDoc(docId + "rejected");
      try {
        await api.patch(`/providers/admin-op/${localProvider.id}/documents/${docId}/status`, {
          status: "rejected",
          rejection_reason: rejectReason,
        });
        const updatedDocs = localProvider.documents.map(d =>
          d.id === docId ? { ...d, status: "rejected", rejection_reason: rejectReason } : d
        );
        const updated = { ...localProvider, documents: updatedDocs };
        setLocalProvider(updated);
        onUpdate(updated);
      } catch (err) {
        console.error("Error al rechazar documento:", err);
      } finally {
        setLoadingDoc(null);
      }
    }
    setRejectReason("");
  };

  const p = localProvider;
  const profileActions = PROFILE_STATUS_ACTIONS[p.status] || [];

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(22,50,68,0.45)",
          zIndex: 100, backdropFilter: "blur(2px)",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(560px, 95vw)", background: "#fff",
        zIndex: 101, overflowY: "auto",
        boxShadow: "-8px 0 40px rgba(22,50,68,0.18)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          background: "#163244", padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", background: "#E7A432",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 20, color: "#163244", flexShrink: 0,
            }}>
              {p.display_name[0]}
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>{p.display_name}</div>
              <div style={{ color: "#8fa3b1", fontSize: 12 }}>{p.email} · DNI {p.dni}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "#8fa3b1",
            fontSize: 22, cursor: "pointer", padding: 4,
          }}>✕</button>
        </div>
        <div style={{ background: "#E7A432", height: 4, flexShrink: 0 }} />

        <div style={{ padding: "24px", flex: 1 }}>

          {/* Estado general */}
          <div style={{
            background: "#f7fafc", borderRadius: 12, padding: "16px 20px",
            marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{ fontSize: 11, color: "#8fa3b1", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Estado del perfil
              </div>
              <Badge status={p.status} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {profileActions.map(a => (
                <ActionBtn
                  key={a.action}
                  label={a.label}
                  color={a.color}
                  loading={loadingProfile === a.action}
                  onClick={() => handleProfileStatus(a.action)}
                />
              ))}
            </div>
          </div>

          {/* Verificaciones */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Identidad", ok: p.identity_verified },
              { label: "Teléfono",  ok: p.phone_verified },
            ].map(v => (
              <div key={v.label} style={{
                flex: 1, padding: "10px 14px", borderRadius: 10,
                background: v.ok ? "#e8f7ef" : "#fdecea",
                border: `1px solid ${v.ok ? "#1b7f4e" : "#e57373"}`,
                fontSize: 12, fontWeight: 600,
                color: v.ok ? "#1b7f4e" : "#c0392b",
                display: "flex", justifyContent: "space-between",
              }}>
                {v.label} verificado
                <span>{v.ok ? "✓" : "✕"}</span>
              </div>
            ))}
          </div>

          {/* Servicios */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#163244",
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
            }}>
              Servicios ({(p.services || []).length})
            </div>
            {(p.services || []).map(s => (
              <div key={s.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderRadius: 8, background: "#f7fafc",
                border: "1px solid #e0e9ef", marginBottom: 6,
              }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#163244" }}>{s.service_name || s.label}</span>
                  <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: 8 }}>Nivel {s.validation_level}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Badge status={s.status} />
                  {(s.status === "pending_documents" || s.status === "pending_review") && (
                    <button
                      onClick={() => handleServiceStatus(s.id, "approved")}
                      style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#1b7f4e", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}
                    >
                      Aprobar
                    </button>
                  )}
                  {s.status === "approved" && (
                    <button
                      onClick={() => handleServiceStatus(s.id, "rejected")}
                      style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#c0392b", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}
                    >
                      Rechazar
                    </button>
                  )}
                  {s.status === "rejected" && (
                    <button
                      onClick={() => handleServiceStatus(s.id, "approved")}
                      style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#1b7f4e", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif" }}
                    >
                      Aprobar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Documentos */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#163244",
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
            }}>
              Documentos ({(p.documents || []).length})
            </div>

            {(p.documents || []).length === 0 && (
              <div style={{
                textAlign: "center", padding: 24,
                background: "#f7fafc", borderRadius: 10,
                border: "2px dashed #e0e9ef", color: "#8fa3b1", fontSize: 13,
              }}>
                📄 Aún no se cargaron documentos.
              </div>
            )}

            {(p.documents || []).map(d => {
              const docStatus = d.verification_status || d.status;
              const actions = DOC_STATUS_ACTIONS[docStatus] || [];
              return (
                <div key={d.id} style={{
                  borderRadius: 10, border: "1.5px solid #e0e9ef",
                  marginBottom: 8, overflow: "hidden",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "11px 14px", background: "#f7fafc",
                  }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>📄 {d.document_type || d.type}</span>
                      <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: 8 }}>
                        {d.service} · {d.created_at ? new Date(d.created_at).toLocaleDateString('es-AR') : d.uploaded_at}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Badge status={docStatus} />
                      {actions.map(a => (
                        <ActionBtn
                          key={a.action}
                          label={a.label}
                          color={a.color}
                          loading={loadingDoc === d.id + a.action}
                          onClick={() => handleDocStatus(d.id, a.action)}
                        />
                      ))}
                    </div>
                  </div>
                  {d.rejection_reason && (
                    <div style={{
                      padding: "8px 14px", background: "#fdecea",
                      fontSize: 12, color: "#c0392b",
                    }}>
                      Motivo de rechazo: {d.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(22,50,68,0.55)",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "32px",
            width: "min(420px, 90vw)",
            boxShadow: "0 8px 40px rgba(22,50,68,0.2)",
          }}>
            <h3 style={{ margin: "0 0 8px", color: "#163244", fontSize: 17, fontWeight: 800 }}>
              ¿Confirmar rechazo?
            </h3>
            <p style={{ margin: "0 0 16px", color: "#5c7a8a", fontSize: 13 }}>
              {rejectModal.type === "profile"
                ? "El prestador será notificado. Podés reactivar el perfil más adelante."
                : "El prestador deberá volver a cargar este documento."}
            </p>
            <textarea
              placeholder="Motivo del rechazo (opcional)..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              style={{
                ...inputStyle, width: "100%", resize: "vertical",
                minHeight: 80, lineHeight: 1.5, marginBottom: 16,
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setRejectModal(null); setRejectReason(""); }}
                style={{
                  padding: "10px 20px", borderRadius: 8,
                  border: "2px solid #cddae4", background: "transparent",
                  color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmReject}
                style={{
                  padding: "10px 20px", borderRadius: 8,
                  background: "#c0392b", border: "none",
                  color: "#fff", fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                Confirmar rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminProvidersPage() {
  const [providers,      setProviders]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [loadingError,   setLoadingError]   = useState("");
  const [search,         setSearch]         = useState("");
  const [filterStatus,   setFilterStatus]   = useState("all");
  const [selected,       setSelected]       = useState(null);
  const [page,           setPage]           = useState(0);
  const PAGE_SIZE = 10;

  // Cargar lista desde GET /providers/admin-op/list
  const fetchProviders = useCallback(async () => {
    setLoading(true); setLoadingError("");
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (search) params.search = search;
      const res = await api.get("/providers/admin-op/list", { params });
      setProviders(res.data.providers || res.data || []);
    } catch (err) {
      console.error("Error cargando prestadores:", err);
      setLoadingError("No se pudo cargar la lista de prestadores.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search]);

  useEffect(() => {
    const timer = setTimeout(fetchProviders, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchProviders]);

  const STATUS_FILTERS = [
    { value: "all",            label: "Todos" },
    { value: "active",         label: "Activos" },
    { value: "pending_review", label: "En revisión" },
    { value: "draft",          label: "Borrador" },
    { value: "suspended",      label: "Suspendidos" },
    { value: "rejected",       label: "Rechazados" },
  ];

  const filtered = useMemo(() => {
    return providers.filter(p => {
      const matchSearch = !search ||
        p.display_name.toLowerCase().includes(search.toLowerCase()) ||
        p.email.toLowerCase().includes(search.toLowerCase()) ||
        p.dni.includes(search);
      const matchStatus = filterStatus === "all" || p.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [providers, search, filterStatus]);

  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const handleUpdate = async (updated) => {
    setProviders(ps => ps.map(p => p.id === updated.id ? updated : p));
    // Recargar el detalle completo desde el backend para tener datos frescos
    try {
      const res = await api.get(`/providers/admin-op/${updated.id}`);
      const fresh = res.data.provider || res.data;
      setProviders(ps => ps.map(p => p.id === fresh.id ? fresh : p));
      setSelected({ documents: [], services: [], ...fresh });
    } catch {
      setSelected({ documents: [], services: [], ...updated });
    }
  };

  const handleSelect = async (provider) => {
    setSelected({ documents: [], services: [], ...provider }); // mostrar drawer inmediatamente con datos parciales
    try {
      const res = await api.get(`/providers/admin-op/${provider.id}`);
      const fresh = res.data.provider || res.data;
      setSelected({ documents: [], services: [], ...fresh });
    } catch (err) {
      console.error("Error cargando detalle:", err);
    }
  };

  // Stats
  const stats = useMemo(() => ({
    total:    providers.length,
    active:   providers.filter(p => p.status === "active").length,
    review:   providers.filter(p => p.status === "pending_review").length,
    suspended:providers.filter(p => p.status === "suspended").length,
  }), [providers]);

  return (
    <>
      <div style={{ fontFamily: "Montserrat, sans-serif", maxWidth: 1100, margin: "0 auto", padding: "32px 20px 80px" }}>

        {/* Page title */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: "#163244" }}>
            Prestadores
          </h1>
          <p style={{ margin: 0, color: "#8fa3b1", fontSize: 14 }}>
            Gestión de perfiles, aprobación de documentos y estado de servicios.
          </p>
        </div>

        {/* Stats */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14, marginBottom: 24,
        }}>
          {[
            { label: "Total",        value: stats.total,     color: "#163244", icon: "👥" },
            { label: "Activos",      value: stats.active,    color: "#1b7f4e", icon: "✓"  },
            { label: "En revisión",  value: stats.review,    color: "#b06a00", icon: "⏳" },
            { label: "Suspendidos",  value: stats.suspended, color: "#c0392b", icon: "⚠"  },
          ].map(s => (
            <div key={s.service_name || s.label} style={{
              background: "#fff", borderRadius: 12,
              padding: "18px 20px",
              boxShadow: "0 2px 10px rgba(22,50,68,0.06)",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: s.color + "18",
                display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 18,
              }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "#8fa3b1", fontWeight: 600 }}>{s.service_name || s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{
          background: "#fff", borderRadius: 12, padding: "16px 20px",
          marginBottom: 16, display: "flex", gap: 12, alignItems: "center",
          flexWrap: "wrap",
          boxShadow: "0 2px 10px rgba(22,50,68,0.06)",
        }}>
          <input
            style={{ ...inputStyle, flex: "1 1 220px", minWidth: 0 }}
            placeholder="🔍  Buscar por nombre, email o DNI..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => { setFilterStatus(f.value); setPage(0); }}
                style={{
                  padding: "7px 14px", borderRadius: 20,
                  border: `1.5px solid ${filterStatus === f.value ? "#163244" : "#e0e9ef"}`,
                  background: filterStatus === f.value ? "#163244" : "transparent",
                  color: filterStatus === f.value ? "#fff" : "#5c7a8a",
                  fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {f.label}
                {f.value !== "all" && (
                  <span style={{
                    marginLeft: 6, background: filterStatus === f.value ? "#E7A432" : "#e0e9ef",
                    color: filterStatus === f.value ? "#163244" : "#5c7a8a",
                    borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 800,
                  }}>
                    {providers.filter(p => p.status === f.value).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: "#fff", borderRadius: 12,
          boxShadow: "0 2px 10px rgba(22,50,68,0.06)",
          overflow: "hidden",
        }}>
          {/* Table header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr 0.8fr 80px",
            padding: "12px 20px", background: "#f7fafc",
            borderBottom: "1px solid #e0e9ef",
            fontSize: 10, fontWeight: 700, color: "#8fa3b1",
            textTransform: "uppercase", letterSpacing: "0.06em", gap: 8,
          }}>
            <div>Prestador</div>
            <div>Servicios</div>
            <div>Estado</div>
            <div>Docs</div>
            <div>Verificado</div>
            <div>Trabajos</div>
            <div></div>
          </div>

          {loading && (
            <div style={{ textAlign: "center", padding: "48px", color: "#8fa3b1", fontSize: 14 }}>
              <span style={{ display: "inline-block", width: 24, height: 24, border: "3px solid #cddae4", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br />
              Cargando prestadores...
            </div>
          )}
          {loadingError && !loading && (
            <div style={{ textAlign: "center", padding: "48px", color: "#c0392b", fontSize: 14 }}>
              ⚠ {loadingError}
              <br />
              <button onClick={fetchProviders} style={{ marginTop: 12, padding: "8px 20px", borderRadius: 8, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Reintentar
              </button>
            </div>
          )}
          {!loading && paginated.length === 0 && !loadingError && (
            <div style={{ textAlign: "center", padding: "48px", color: "#8fa3b1", fontSize: 14 }}>
              No se encontraron prestadores con esos filtros.
            </div>
          )}
          {!loading && !loadingError && paginated.map((p, i) => {
            const pendingDocs = (p.documents || []).filter(d => (d.verification_status || d.status) === 'pending').length;
            return (
              <div
                key={p.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr 0.8fr 80px",
                  padding: "14px 20px", gap: 8,
                  borderBottom: i < paginated.length - 1 ? "1px solid #f0f5f8" : "none",
                  alignItems: "center",
                  transition: "background 0.15s",
                  cursor: "default",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#fafcfe"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {/* Prestador */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: "#163244", display: "flex", alignItems: "center",
                    justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#E7A432",
                  }}>
                    {p.display_name[0]}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontWeight: 700, fontSize: 13, color: "#163244",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {p.display_name}
                    </div>
                    <div style={{ fontSize: 11, color: "#8fa3b1" }}>{p.email}</div>
                  </div>
                </div>

                {/* Servicios */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {(p.services || []).slice(0, 2).map(s => (
                    <span key={s.id} style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 10,
                      background: "#f0f5f8", color: "#163244", fontWeight: 600,
                    }}>
                      {s.service_name || s.label}
                    </span>
                  ))}
                  {(p.services || []).length > 2 && (
                    <span style={{ fontSize: 10, color: "#8fa3b1" }}>+{(p.services || []).length - 2}</span>
                  )}
                </div>

                {/* Estado */}
                <div><Badge status={p.status} /></div>

                {/* Docs */}
                <div>
                  {(p.documents || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: "#aab8c2" }}>Sin docs</span>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12, color: "#163244", fontWeight: 600 }}>
                        {(p.documents || []).length}
                      </span>
                      {pendingDocs > 0 && (
                        <span style={{
                          fontSize: 10, padding: "2px 7px", borderRadius: 10,
                          background: "#fff3e0", color: "#b06a00", fontWeight: 700,
                        }}>
                          {pendingDocs} pendiente{pendingDocs > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Verificado */}
                <div style={{ fontSize: 14, display: "flex", gap: 6 }}>
                  <span title="Identidad">{p.identity_verified ? "🪪✓" : "🪪✕"}</span>
                </div>

                {/* Trabajos */}
                <div style={{ fontSize: 13, fontWeight: 600, color: "#163244" }}>
                  {p.jobs_completed}
                  {p.rating_avg && (
                    <span style={{ fontSize: 11, color: "#E7A432", marginLeft: 4 }}>
                      ⭐{p.rating_avg}
                    </span>
                  )}
                </div>

                {/* Acción */}
                <div>
                  <button
                    onClick={() => handleSelect(p)}
                    style={{
                      padding: "6px 14px", borderRadius: 6,
                      background: "#163244", border: "none", color: "#fff",
                      fontFamily: "Montserrat, sans-serif", fontWeight: 600,
                      fontSize: 11, cursor: "pointer",
                    }}
                  >
                    Ver →
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: "flex", justifyContent: "center", alignItems: "center",
            gap: 8, marginTop: 20,
          }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{
                padding: "7px 16px", borderRadius: 8,
                border: "1.5px solid #e0e9ef", background: "#fff",
                color: page === 0 ? "#cddae4" : "#163244",
                fontFamily: "Montserrat, sans-serif", fontWeight: 600,
                fontSize: 12, cursor: page === 0 ? "not-allowed" : "pointer",
              }}
            >← Anterior</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                style={{
                  width: 34, height: 34, borderRadius: 8,
                  border: `1.5px solid ${page === i ? "#163244" : "#e0e9ef"}`,
                  background: page === i ? "#163244" : "#fff",
                  color: page === i ? "#fff" : "#163244",
                  fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                  fontSize: 13, cursor: "pointer",
                }}
              >{i + 1}</button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              style={{
                padding: "7px 16px", borderRadius: 8,
                border: "1.5px solid #e0e9ef", background: "#fff",
                color: page === totalPages - 1 ? "#cddae4" : "#163244",
                fontFamily: "Montserrat, sans-serif", fontWeight: 600,
                fontSize: 12, cursor: page === totalPages - 1 ? "not-allowed" : "pointer",
              }}
            >Siguiente →</button>
          </div>
        )}

        <div style={{ marginTop: 12, textAlign: "center", fontSize: 12, color: "#8fa3b1" }}>
          {filtered.length} prestador{filtered.length !== 1 ? "es" : ""} encontrado{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Drawer */}
      {selected && (
        <ProviderDrawer
          provider={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input:focus { border-color: #163244 !important; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
